<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import DeleteDialog from 'dashboard/components-next/captain/pageComponents/DeleteDialog.vue';
import McpServerCard from 'dashboard/components-next/captain/assistant/McpServerCard.vue';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import CaptainPaywall from 'dashboard/components-next/captain/pageComponents/Paywall.vue';
import CreateMcpServerDialog from 'dashboard/components-next/captain/pageComponents/mcpServer/CreateMcpServerDialog.vue';
import ToolTesterDialog from 'dashboard/components-next/captain/pageComponents/mcpServer/ToolTesterDialog.vue';
import AssistantSelector from 'dashboard/components-next/captain/pageComponents/AssistantSelector.vue';
import McpServerPageEmptyState from 'dashboard/components-next/captain/pageComponents/emptyStates/McpServerPageEmptyState.vue';
import FeatureSpotlightPopover from 'dashboard/components-next/feature-spotlight/FeatureSpotlightPopover.vue';

const store = useStore();

const uiFlags = useMapGetter('captainMcpServers/getUIFlags');
const mcpServers = useMapGetter('captainMcpServers/getRecords');
const assistants = useMapGetter('captainAssistants/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingList);
const mcpServersMeta = useMapGetter('captainMcpServers/getMeta');
const selectedAssistant = ref('all');

const selectedMcpServer = ref(null);
const deleteMcpServerDialog = ref(null);

const handleDelete = () => {
  deleteMcpServerDialog.value.dialogRef.open();
};

const showCreateDialog = ref(false);
const createMcpServerDialog = ref(null);

const showToolTesterDialog = ref(false);
const toolTesterDialog = ref(null);

const shouldShowAssistantSelector = computed(() => {
  if (assistants.value.length === 0) return false;

  return !isFetching.value;
});

const handleCreateMcpServer = () => {
  selectedMcpServer.value = null;
  showCreateDialog.value = true;
  nextTick(() => createMcpServerDialog.value.dialogRef.open());
};

const handleEditMcpServer = () => {
  showCreateDialog.value = true;
  nextTick(() => createMcpServerDialog.value.dialogRef.open());
};

const handleCreateDialogClose = () => {
  showCreateDialog.value = false;
  selectedMcpServer.value = null;
};

const handleToolTesterDialogClose = () => {
  showToolTesterDialog.value = false;
};

const handleViewTools = () => {
  showToolTesterDialog.value = true;
  nextTick(() => toolTesterDialog.value.dialogRef.open());
};

const handleAction = ({ action, id }) => {
  selectedMcpServer.value = mcpServers.value.find(server => id === server.id);

  nextTick(() => {
    if (action === 'delete') {
      handleDelete();
    } else if (action === 'edit') {
      handleEditMcpServer();
    } else if (action === 'view_tools') {
      handleViewTools();
    }
  });
};

const fetchMcpServers = (page = 1) => {
  const filterParams = { page };

  if (selectedAssistant.value !== 'all') {
    filterParams.assistant_id = selectedAssistant.value;
  }
  store.dispatch('captainMcpServers/get', filterParams);
};

const handleAssistantFilterChange = assistant => {
  selectedAssistant.value = assistant;
  fetchMcpServers();
};

const onPageChange = page => fetchMcpServers(page);

const onDeleteSuccess = () => {
  if (mcpServers.value?.length === 0 && mcpServersMeta.value?.page > 1) {
    onPageChange(mcpServersMeta.value.page - 1);
  }
};

onMounted(() => {
  if (!assistants.value.length) {
    store.dispatch('captainAssistants/get');
  }
  fetchMcpServers();
});
</script>

<template>
  <PageLayout
    :header-title="$t('CAPTAIN.MCP_SERVERS.HEADER')"
    :button-label="$t('CAPTAIN.MCP_SERVERS.ADD_NEW')"
    :button-policy="['administrator']"
    :total-count="mcpServersMeta.totalCount"
    :current-page="mcpServersMeta.page"
    :show-pagination-footer="!isFetching && !!mcpServers.length"
    :is-fetching="isFetching"
    :is-empty="!mcpServers.length"
    :feature-flag="FEATURE_FLAGS.CAPTAIN"
    @update:current-page="onPageChange"
    @click="handleCreateMcpServer"
  >
    <template #knowMore>
      <FeatureSpotlightPopover
        :button-label="$t('CAPTAIN.HEADER_KNOW_MORE')"
        :title="$t('CAPTAIN.MCP_SERVERS.EMPTY_STATE.FEATURE_SPOTLIGHT.TITLE')"
        :note="$t('CAPTAIN.MCP_SERVERS.EMPTY_STATE.FEATURE_SPOTLIGHT.NOTE')"
        fallback-thumbnail="/assets/images/dashboard/captain/mcp-servers-popover-light.svg"
        fallback-thumbnail-dark="/assets/images/dashboard/captain/mcp-servers-popover-dark.svg"
        learn-more-url="https://modelcontextprotocol.io"
      />
    </template>

    <template #emptyState>
      <McpServerPageEmptyState @click="handleCreateMcpServer" />
    </template>

    <template #paywall>
      <CaptainPaywall />
    </template>

    <template #controls>
      <div v-if="shouldShowAssistantSelector" class="mb-4 -mt-3 flex gap-3">
        <AssistantSelector
          :assistant-id="selectedAssistant"
          @update="handleAssistantFilterChange"
        />
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <McpServerCard
          v-for="server in mcpServers"
          :id="server.id"
          :key="server.id"
          :name="server.name"
          :title="server.title"
          :description="server.description"
          :assistant="server.assistant"
          :transport-type="server.transport_type"
          :enabled="server.enabled"
          :icon="server.icon"
          :created-at="server.created_at"
          @action="handleAction"
        />
      </div>
    </template>

    <CreateMcpServerDialog
      v-if="showCreateDialog"
      ref="createMcpServerDialog"
      :mcp-server="selectedMcpServer"
      @close="handleCreateDialogClose"
    />
    <ToolTesterDialog
      v-if="showToolTesterDialog && selectedMcpServer"
      ref="toolTesterDialog"
      :mcp-server="selectedMcpServer"
      @close="handleToolTesterDialogClose"
    />
    <DeleteDialog
      v-if="selectedMcpServer"
      ref="deleteMcpServerDialog"
      :entity="selectedMcpServer"
      type="McpServers"
      @delete-success="onDeleteSuccess"
    />
  </PageLayout>
</template>
